import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';

import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { IExcepcion, IFilter, IPopulate, IQueryParam } from 'modelos/src';
import { ExcepcionsService } from '../../../../auxiliares/servicios/http/excepcions.service';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../auxiliares/tabla/tabla.component';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import { CrearExcepcionComponent } from '../crear-excepcion/crear-excepcion.component';
import { Subscription } from 'rxjs';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { Router } from '@angular/router';
import { IRegExpSearch } from '../../../../auxiliares/tabla/filtro/filtro.component';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';

@Component({
  selector: 'app-listado-excepciones',
  templateUrl: './listado.component.html',
  styleUrls: ['./listado.component.scss'],
  standalone: true,
  imports: [AuxiliaresModule],
})
export class ListadoExcepcionesComponent implements OnInit, OnDestroy {
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private service = inject(ExcepcionsService);
  private formularios = inject(FormularioDialogService);
  private dialogService = inject(DialogService);
  helper = inject(HelperService);
  private router = inject(Router);
  lista2 = inject(Lista2Service);

  @Input() idEntidad?: string;
  @Input() tipoEntidad?: 'Cliente' | 'Alarma';
  @Input() tipoExcepcion?: 'Control Horario';
  @Input() buffer?: { fechaDesde: string; fechaHasta: string }[];
  @Output() bufferChange = new EventEmitter<
    { fechaDesde: string; fechaHasta: string }[]
  >();

  public name = 'ListadoExcepcionesComponent';
  public excepciones?: IExcepcion[];
  public totalCount = 0;
  public columnas?: IColumnas<IExcepcion>[];
  public excepciones$?: Subscription;

  public botonCrear: ICrearBoton = {
    mostrar: true,
    tooltip: 'Crear excepción',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };

  public search: IRegExpSearch = {
    fields: ['tipoEntidad', 'tipoExcepcion'],
  };

  public filter: IFilter<IExcepcion> = {};
  public populate: IPopulate[] = [
    {
      path: 'cliente',
      select: 'nombre',
    },
    {
      path: 'ancestros',
      select: 'nombre',
    },
  ];
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: '-fechaCreacion',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
  };

  ngOnInit(): void {
    this.configurarColumnas();

    if (this.buffer !== undefined) {
      this.excepciones = this.buffer as unknown as IExcepcion[];
      this.totalCount = this.buffer.length;
      return;
    }

    // Aplicar filtros basados en los inputs
    if (this.idEntidad) {
      this.filter.idEntidad = this.idEntidad;
    }
    if (this.tipoEntidad) {
      this.filter.tipoEntidad = this.tipoEntidad;
    }
    if (this.tipoExcepcion) {
      this.filter.tipoExcepcion = this.tipoExcepcion;
    }

    // Actualizar query con los filtros
    this.query.filter = JSON.stringify(this.filter);

    this.listar();
  }

  ngOnDestroy(): void {
    this.excepciones$?.unsubscribe();
  }

  public async listar(): Promise<void> {
    // Con buffer (alarma todavía sin crear) los datos son locales: ordenar o
    // paginar no debe traer excepciones del backend.
    if (this.buffer !== undefined) return;
    this.excepciones$?.unsubscribe();
    this.excepciones$ = this.lista2
      .excepcions(this.query, 30)
      .subscribe(async (data) => {
        if (HelperService.cambiaronLosDatos(this.excepciones, data.datos)) {
          this.excepciones = data.datos;
          this.totalCount = data.totalCount;
        }
      });
  }

  configurarColumnas(): void {
    const enModoBuffer = this.buffer !== undefined;
    this.columnas = [
      {
        header: {
          label: 'Cliente',
          sort: 'cliente.nombre',
          hidden: enModoBuffer,
        },
        row: {
          field: 'cliente',
          html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          noData: 'Sin cliente',
        },
      },
      {
        header: {
          label: 'Tipo de entidad',
          sort: 'tipoEntidad',
          hidden: !!this.tipoEntidad || enModoBuffer,
        },
        row: {
          field: 'tipoEntidad',
        },
      },
      {
        header: {
          label: 'ID de entidad',
          sort: 'idEntidad',
          hidden: !!this.idEntidad || enModoBuffer,
        },
        row: {
          field: 'idEntidad',
        },
      },
      {
        header: {
          label: 'Tipo de excepción',
          sort: 'tipoExcepcion',
          hidden: !!this.tipoExcepcion || enModoBuffer,
        },
        row: {
          field: 'tipoExcepcion',
        },
      },
      {
        header: {
          label: 'Fecha desde',
          sort: 'fechaDesde',
        },
        row: {
          fecha: (dato) => dato.fechaDesde,
          noData: 'Sin fecha',
        },
      },
      {
        header: {
          label: 'Fecha hasta',
          sort: 'fechaHasta',
        },
        row: {
          fecha: (dato) => dato.fechaHasta,
          noData: 'Sin fecha',
        },
      },
      {
        header: {
          label: 'Fecha de creación',
          sort: 'fechaCreacion',
          hidden: enModoBuffer,
        },
        row: {
          fecha: (dato) => dato.fechaCreacion,
          noData: 'Sin fecha',
        },
      },
      {
        header: {
          label: 'Acciones',
        },
        row: {
          acciones: [
            {
              tipo: 'material',
              color: 'warn',
              icon: 'delete',
              tooltip: 'Eliminar',
              click: (dato) => this.eliminar(dato),
            },
          ],
        },
      },
    ];
  }

  crear(): void {
    if (this.buffer !== undefined) {
      const dialogRef = this.formularios.abrir(CrearExcepcionComponent, {
        tamano: 'chico',
        data: {
          soloLocal: true,
          tipoEntidad: this.tipoEntidad,
          tipoExcepcion: this.tipoExcepcion,
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          const newBuffer = [
            ...this.buffer!,
            { fechaDesde: result.fechaDesde, fechaHasta: result.fechaHasta },
          ];
          this.buffer = newBuffer;
          this.excepciones = newBuffer as unknown as IExcepcion[];
          this.totalCount = newBuffer.length;
          this.bufferChange.emit(newBuffer);
        }
      });
      return;
    }

    const dialogRef = this.formularios.abrir(CrearExcepcionComponent, {
      tamano: 'chico',
      data: {
        idEntidad: this.idEntidad,
        tipoEntidad: this.tipoEntidad,
        tipoExcepcion: this.tipoExcepcion,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.listar();
      }
    });
  }

  editar(excepcion: IExcepcion): void {
    const dialogRef = this.formularios.abrir(CrearExcepcionComponent, {
      tamano: 'chico',
      data: excepcion,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.listar();
      }
    });
  }

  async eliminar(excepcion: IExcepcion): Promise<void> {
    if (this.buffer !== undefined) {
      const index = this.buffer.findIndex(
        (e) =>
          e.fechaDesde === excepcion.fechaDesde &&
          e.fechaHasta === excepcion.fechaHasta,
      );
      if (index !== -1) {
        const newBuffer = this.buffer.filter((_, i) => i !== index);
        this.buffer = newBuffer;
        this.excepciones = newBuffer as unknown as IExcepcion[];
        this.totalCount = newBuffer.length;
        this.bufferChange.emit(newBuffer);
      }
      return;
    }

    const confirmado = await this.dialogService.confirm(
      'Eliminar excepción',
      '¿Querés eliminar esta excepción?',
    );
    if (!confirmado) return;
    try {
      await this.service.delete(excepcion._id!);
      this.helper.notifSuccess('Excepción eliminada');
      await this.listar();
    } catch (error) {
      this.helper.notifError(error);
    }
  }
}
